import {
  AlertTriangle,
  BookOpen,
  Building2,
  Calendar,
  ClipboardList,
  Droplets,
  FileText,
  Flame,
  Globe2,
  Landmark,
  Mail,
  MapPin,
  Phone,
  Shield,
  Sprout,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { siteConfig } from '../config/site';

type ContactItem = {
  title: string;
  description: string;
  phone?: string;
  phoneLabel?: string;
  email?: string;
  address?: string;
  icon: typeof Phone;
};

export function Resources() {
  const quickContacts: ContactItem[] = [
    {
      title: 'City Hall',
      description: 'Main line for city services and departments',
      phone: '+16612675100',
      phoneLabel: '(661) 267-5100',
      address: `38300 Sierra Hwy, ${siteConfig.city}, ${siteConfig.state} 93550`,
      icon: Phone,
    },
    {
      title: 'Police (Non-Emergency)',
      description: 'Palmdale Sheriff\'s Station (LASD)',
      phone: '+16612722400',
      phoneLabel: '(661) 272-2400',
      address: `750 E Ave Q, ${siteConfig.city}, ${siteConfig.state} 93550`,
      icon: Shield,
    },
    {
      title: 'Fire Department',
      description: 'LA County Fire - multiple stations',
      phone: '+16619477244',
      phoneLabel: '(661) 947-7244',
      icon: Flame,
    },
    {
      title: 'Hospital',
      description: 'Palmdale Regional Medical Center',
      phone: '+16613825000',
      phoneLabel: '(661) 382-5000',
      address: `38600 Medical Center Dr, ${siteConfig.city}, ${siteConfig.state} 93551`,
      icon: AlertTriangle,
    },
    {
      title: 'Emergency',
      description: 'Life-threatening emergencies',
      phone: '911',
      phoneLabel: '911',
      icon: AlertTriangle,
    },
    {
      title: 'Library',
      description: 'Palmdale City Library',
      phone: '+16612675600',
      phoneLabel: '(661) 267-5600',
      address: `700 E Palmdale Blvd, ${siteConfig.city}, ${siteConfig.state} 93550`,
      icon: BookOpen,
    },
  ];

  const councilMembers = [
    {
      title: 'Mayor',
      name: 'Eric Ohlsen',
      phone: '',
      phoneLabel: 'Contact via City Hall',
      email: '',
    },
    {
      title: 'Mayor Pro Tem',
      name: 'Austin Bishop',
      phone: '',
      phoneLabel: 'Contact via City Hall',
      email: '',
    },
    {
      title: 'Councilmember',
      name: 'Richard J. Loa',
      phone: '',
      phoneLabel: 'Contact via City Hall',
      email: '',
    },
    {
      title: 'Councilmember',
      name: 'Laura Bettencourt',
      phone: '',
      phoneLabel: 'Contact via City Hall',
      email: '',
    },
    {
      title: 'Councilmember',
      name: 'Andrea Alarcón',
      phone: '',
      phoneLabel: 'Contact via City Hall',
      email: '',
    },
  ];

  const departments = [
    {
      title: 'City Manager',
      name: 'Salvador "Sal" Mendez',
      phone: '+16612675100',
      phoneLabel: '(661) 267-5100',
      email: 'Contact via City Hall',
      address: `38300 Sierra Hwy, ${siteConfig.city}, ${siteConfig.state} 93550`,
    },
    {
      title: 'Building & Safety',
      name: 'Building Department',
      phone: '+16612675353',
      phoneLabel: '(661) 267-5353',
      email: 'Contact via City Hall',
      address: `38250 Sierra Hwy, ${siteConfig.city}, ${siteConfig.state} 93550`,
    },
    {
      title: 'Planning Department',
      name: 'Planning Department',
      phone: '+16612675200',
      phoneLabel: '(661) 267-5200',
      email: 'Contact via City Hall',
      address: `38250 Sierra Hwy, ${siteConfig.city}, ${siteConfig.state} 93550`,
    },
    {
      title: 'Parks & Recreation',
      name: 'Parks & Recreation Department',
      phone: '+16612675611',
      phoneLabel: '(661) 267-5611',
      email: 'Contact via City Hall',
      address: `827 E Ave Q-9, ${siteConfig.city}, ${siteConfig.state} 93550`,
    },
    {
      title: 'Public Works',
      name: 'Public Works Department',
      phone: '+16612675300',
      phoneLabel: '(661) 267-5300',
      email: 'Contact via City Hall',
    },
    {
      title: 'Code Enforcement',
      name: 'Code Enforcement Division',
      phone: '+16612675234',
      phoneLabel: '(661) 267-5234',
      email: 'Contact via City Hall',
    },
  ];

  return (
    <div className="min-h-screen bg-surface text-on-surface">
      <SEO
        title={siteConfig.seo.pages.resourcesTitle}
        description={siteConfig.seo.pages.resourcesDescription}
        path="/resources"
      />

      {/* Hero */}
      <section className="relative bg-surface-container-low overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-tertiary/10" />
        <div className="container mx-auto px-4 py-16 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
            <div>
              <p className="text-primary font-semibold mb-3">City services and trusted contacts</p>
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">Community Resources</h1>
              <p className="text-lg text-on-surface-variant mb-6">
                Your guide to city services, departments, and essential contacts in {siteConfig.city}.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary">Contact Us</Link>
                <a
                  href="https://www.cityofpalmdaleca.gov/"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline"
                >
                  Official City Website
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="card bg-surface-container-high">
                <Landmark className="w-6 h-6 text-primary mb-3" />
                <h3 className="text-lg font-semibold mb-2">City Snapshot</h3>
                <p className="text-on-surface-variant">
                  Founded in 1886 as "Palmenthal" by Swiss and German settlers, incorporated August 24, 1962, and became a charter city in 2009. 
                  {siteConfig.city} is the aerospace capital of America with a population of 169,450+ (2020 census).
                </p>
              </div>
              <div className="card bg-surface-container-high">
                <Calendar className="w-6 h-6 text-primary mb-3" />
                <h3 className="text-lg font-semibold mb-2">City Hall Hours</h3>
                <p className="text-on-surface-variant">Monday - Thursday, 7:30 AM - 6:00 PM (Closed Fridays)</p>
                <p className="text-on-surface-variant">38300 Sierra Highway, {siteConfig.city}, {siteConfig.state} 93550</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contacts */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <h2 className="text-3xl font-display font-bold mb-3">Quick contacts</h2>
              <p className="text-on-surface-variant text-lg">
                The most-requested numbers for residents and visitors.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickContacts.map((contact) => {
              const Icon = contact.icon;
              return (
                <div key={contact.title} className="card bg-surface-container-low">
                  <div className="flex items-start gap-3">
                    <Icon className="w-6 h-6 text-primary mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{contact.title}</h3>
                      <p className="text-on-surface-variant mb-3">{contact.description}</p>
                      {contact.phone && (
                        <a className="text-primary font-semibold" href={`tel:${contact.phone}`}>
                          {contact.phoneLabel}
                        </a>
                      )}
                      {contact.address && (
                        <p className="text-sm text-on-surface-variant mt-2">{contact.address}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* City Government */}
      <section className="py-16 bg-surface-container-low">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Landmark className="w-7 h-7 text-primary" />
                <h2 className="text-3xl font-display font-bold">City government</h2>
              </div>
              <p className="text-on-surface-variant text-lg mb-8">
                City Council meetings are held the 1st and 3rd Tuesday of every month at 5:00 PM at Council Chambers,
                38300 Sierra Highway, Suite B, {siteConfig.city}, {siteConfig.state} 93550.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {councilMembers.map((member) => (
                  <div key={`${member.title}-${member.name}`} className="card">
                    <p className="text-sm text-primary font-semibold">{member.title}</p>
                    <h3 className="text-lg font-semibold mb-2">{member.name}</h3>
                    {member.phone ? (
                      <a className="text-primary" href={`tel:${member.phone}`}>
                        {member.phoneLabel}
                      </a>
                    ) : (
                      <p className="text-on-surface-variant">{member.phoneLabel}</p>
                    )}
                    {member.email && (
                      <div className="mt-2 flex items-center gap-2 text-on-surface-variant">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${member.email}`} className="hover:text-primary">
                          {member.email}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="card bg-surface-container-high">
                <Calendar className="w-6 h-6 text-primary mb-3" />
                <h3 className="text-lg font-semibold mb-2">Meeting schedule</h3>
                <p className="text-on-surface-variant">City Council: 1st & 3rd Tuesday, 5:00 PM</p>
                <p className="text-on-surface-variant">Planning Commission: Contact City Hall for schedule</p>
              </div>
              <div className="card bg-surface-container-high">
                <ClipboardList className="w-6 h-6 text-primary mb-3" />
                <h3 className="text-lg font-semibold mb-2">Agendas & recordings</h3>
                <p className="text-on-surface-variant mb-3">
                  Council agendas and recorded meetings are available through the city's CivicEngage portal.
                </p>
                <a
                  href="https://www.cityofpalmdaleca.gov/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary font-semibold"
                >
                  View City Council resources
                </a>
              </div>
              <div className="card bg-surface-container-high">
                <Shield className="w-6 h-6 text-primary mb-3" />
                <h3 className="text-lg font-semibold mb-2">Public safety</h3>
                <p className="text-on-surface-variant">Emergency: 911</p>
                <p className="text-on-surface-variant">Police (LASD Palmdale Station): (661) 272-2400</p>
                <p className="text-on-surface-variant">Fire Stations: 24, 37, 93, 131, 136 (LA County Fire)</p>
                <p className="text-on-surface-variant mt-2">
                  {siteConfig.city} is served by Los Angeles County Sheriff's Department and LA County Fire Department.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* City Departments */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-7 h-7 text-primary" />
            <h2 className="text-3xl font-display font-bold">City departments</h2>
          </div>
          <p className="text-on-surface-variant text-lg mb-8">
            Direct lines for key departments and leadership.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.map((dept) => (
              <div key={dept.title} className="card bg-surface-container-low">
                <h3 className="text-lg font-semibold mb-1">{dept.title}</h3>
                <p className="text-on-surface-variant mb-3">{dept.name}</p>
                <a className="text-primary font-semibold" href={`tel:${dept.phone}`}>
                  {dept.phoneLabel}
                </a>
                {dept.secondaryPhone && (
                  <p className="text-on-surface-variant mt-1">
                    Desk: <a href={`tel:${dept.secondaryPhone}`} className="text-primary">{dept.secondaryLabel}</a>
                  </p>
                )}
                {dept.email && (
                  <div className="mt-2 flex items-center gap-2 text-on-surface-variant">
                    <Mail className="w-4 h-4" />
                    {dept.email.includes('@') ? (
                      <a href={`mailto:${dept.email}`} className="hover:text-primary">
                        {dept.email}
                      </a>
                    ) : (
                      <span>{dept.email}</span>
                    )}
                  </div>
                )}
                {dept.address && (
                  <p className="text-sm text-on-surface-variant mt-3">{dept.address}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Public Utilities */}
      <section className="py-16 bg-surface-container-low">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Droplets className="w-7 h-7 text-primary" />
            <h2 className="text-3xl font-display font-bold">Public utilities</h2>
          </div>
          <p className="text-on-surface-variant text-lg mb-8">
            Utility services and infrastructure information for {siteConfig.city} residents and businesses.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card bg-surface">
              <h3 className="text-lg font-semibold mb-2">Sewer Maintenance & Utilities</h3>
              <p className="text-on-surface-variant">City of {siteConfig.city} Public Works Department</p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 text-on-surface-variant">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+16612675272" className="text-primary">(661) 267-5272</a>
                </div>
                <div className="flex items-center gap-2 text-on-surface-variant">
                  <MapPin className="w-4 h-4" />
                  <span>Contact City Hall for service requests</span>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-on-surface-variant font-semibold">Street Sweeping & Park Maintenance</p>
                <p className="text-on-surface-variant">Public Works: (661) 267-5300</p>
              </div>
              <div className="mt-4">
                <p className="text-on-surface-variant font-semibold">Graffiti Removal</p>
                <p className="text-on-surface-variant">Graffiti Hotline: (661) 947-7433</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="card bg-surface">
                <h3 className="text-lg font-semibold mb-2">Animal Care</h3>
                <p className="text-on-surface-variant">
                  Los Angeles County Animal Care Center ({siteConfig.city})
                </p>
                <div className="mt-3 flex items-center gap-2 text-on-surface-variant">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+16615752888" className="text-primary">(661) 575-2888</a>
                </div>
                <p className="text-on-surface-variant mt-2">Animal control and care services for the Antelope Valley.</p>
              </div>
              <div className="card bg-surface">
                <h3 className="text-lg font-semibold mb-2">Waste management</h3>
                <p className="text-on-surface-variant">Trash and recycling services: Contact City Hall for current provider information.</p>
                <p className="text-on-surface-variant">Commercial and residential pickup available through various providers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Services */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-7 h-7 text-primary" />
            <h2 className="text-3xl font-display font-bold">Community services</h2>
          </div>
          <p className="text-on-surface-variant text-lg mb-8">
            Libraries, senior services, parks, and local programs that keep {siteConfig.city} connected.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="card bg-surface-container-low">
              <BookOpen className="w-6 h-6 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">{siteConfig.city} City Library</h3>
              <p className="text-on-surface-variant">700 E Palmdale Blvd, {siteConfig.city}, {siteConfig.state} 93550</p>
              <a href="tel:+16612675600" className="text-primary font-semibold">(661) 267-5600</a>
              <p className="text-on-surface-variant mt-2">Full-service public library serving the community.</p>
            </div>
            <div className="card bg-surface-container-low">
              <Users className="w-6 h-6 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Legacy Commons for Active Seniors</h3>
              <p className="text-on-surface-variant">930 E Ave Q-9, {siteConfig.city}, {siteConfig.state} 93550</p>
              <a href="tel:+16612675904" className="text-primary font-semibold">(661) 267-5904</a>
              <p className="text-on-surface-variant mt-2">Senior activities, resources, and community programs.</p>
            </div>
            <div className="card bg-surface-container-low">
              <Sprout className="w-6 h-6 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Major Parks & Recreation</h3>
              <p className="text-on-surface-variant">
                <strong>Palmdale Oasis Park:</strong> 3850 E Ave S (33 acres, DryTown Water Park, recreation center)
              </p>
              <p className="text-on-surface-variant mt-2">
                <strong>Marie Kerr Park:</strong> 39700 30th St W (recreation center, pool, amphitheater)
              </p>
              <p className="text-on-surface-variant mt-2">
                <strong>Sam Yellen Park & Dog Park:</strong> 5100 E Ave S
              </p>
            </div>
            <div className="card bg-surface-container-low">
              <Users className="w-6 h-6 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Community Programs</h3>
              <p className="text-on-surface-variant">
                <strong>SAVES:</strong> 1002 E Ave Q-12 — (661) 267-5191 (food assistance)
              </p>
              <p className="text-on-surface-variant mt-2">Youth Recreation Scholarships available</p>
              <p className="text-on-surface-variant">Housing Resources for first-time homebuyers and veterans</p>
            </div>
            <div className="card bg-surface-container-low">
              <Building2 className="w-6 h-6 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Cultural Facilities</h3>
              <p className="text-on-surface-variant">
                <strong>Chimbole Cultural Center:</strong> 38350 Sierra Hwy — (661) 267-5656
              </p>
              <p className="text-on-surface-variant mt-2">
                <strong>Palmdale Playhouse:</strong> 38334 10th St E — (661) 267-5684
              </p>
            </div>
            <div className="card bg-surface-container-low">
              <ClipboardList className="w-6 h-6 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Schools & Education</h3>
              <p className="text-on-surface-variant">
                Schools serving {siteConfig.city} are part of the Los Angeles County educational system.
              </p>
              <p className="text-on-surface-variant mt-2">Contact City Hall or school districts directly for enrollment information.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Operating Hours */}
      <section className="py-16 bg-surface-container-low">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-7 h-7 text-primary" />
            <h2 className="text-3xl font-display font-bold">Operating hours summary</h2>
          </div>
          <div className="card bg-surface overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-outline-variant">
                  <th className="py-3 px-2 text-on-surface">Service</th>
                  <th className="py-3 px-2 text-on-surface">Hours</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-outline-variant">
                  <td className="py-3 px-2 text-on-surface-variant">City Hall</td>
                  <td className="py-3 px-2 text-on-surface">Monday - Thursday, 7:30 AM - 6:00 PM (Closed Fridays)</td>
                </tr>
                <tr className="border-b border-outline-variant">
                  <td className="py-3 px-2 text-on-surface-variant">Parks & Recreation</td>
                  <td className="py-3 px-2 text-on-surface">Contact (661) 267-5611 for facility hours</td>
                </tr>
                <tr className="border-b border-outline-variant">
                  <td className="py-3 px-2 text-on-surface-variant">City Council Meetings</td>
                  <td className="py-3 px-2 text-on-surface">1st & 3rd Tuesday, 5:00 PM</td>
                </tr>
                <tr>
                  <td className="py-3 px-2 text-on-surface-variant">Library</td>
                  <td className="py-3 px-2 text-on-surface">Contact (661) 267-5600 for current hours</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Documents & Forms */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-7 h-7 text-primary" />
            <h2 className="text-3xl font-display font-bold">Business licenses & permits</h2>
          </div>
          <p className="text-on-surface-variant text-lg mb-6">
            Essential business and permit information for {siteConfig.city} residents and businesses.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Business Licensing</h3>
              <p className="text-on-surface-variant mb-3">Contact the Economic Development office for business licenses and permits.</p>
              <div className="flex items-center gap-2 text-on-surface-variant">
                <Phone className="w-4 h-4" />
                <a href="tel:+16612478384" className="text-primary">(661) 247-8384</a>
              </div>
              <p className="text-on-surface-variant mt-2">Economic Development: (661) 267-5125</p>
            </div>
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Building & Planning Permits</h3>
              <p className="text-on-surface-variant mb-3">Building permits and planning applications through Development Services.</p>
              <p className="text-on-surface-variant">Building & Safety: (661) 267-5353</p>
              <p className="text-on-surface-variant">Planning Department: (661) 267-5200</p>
              <p className="text-on-surface-variant mt-2">38250 Sierra Hwy, {siteConfig.city}, {siteConfig.state} 93550</p>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhood Watch */}
      <section className="py-16 bg-surface-container-low">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-7 h-7 text-primary" />
            <h2 className="text-3xl font-display font-bold">Community safety & engagement</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card bg-surface">
              <h3 className="text-lg font-semibold mb-2">Palmdale Engaged / Neighborhood Watch</h3>
              <p className="text-on-surface-variant mb-3">Active community policing and neighborhood integration efforts.</p>
              <p className="text-on-surface-variant">Contact Public Safety Coordination: (661) 267-5170</p>
              <p className="text-on-surface-variant">Emergency Services: (661) 267-5542</p>
            </div>
            <div className="card bg-surface">
              <h3 className="text-lg font-semibold mb-2">Charter City Status</h3>
              <p className="text-on-surface-variant">
                {siteConfig.city} became a charter city in November 2009, operating under a Council-Manager form of government.
                This provides greater local control and flexibility in city operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* External Links */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Globe2 className="w-7 h-7 text-primary" />
            <h2 className="text-3xl font-display font-bold">External resources</h2>
          </div>
          <p className="text-on-surface-variant text-lg mb-8">
            Official agencies and online services connected to {siteConfig.city}.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Official city website</h3>
              <p className="text-on-surface-variant mb-3">News, agendas, and citywide updates.</p>
              <a
                href="https://www.cityofpalmdaleca.gov/"
                target="_blank"
                rel="noreferrer"
                className="text-primary font-semibold"
              >
                Visit cityofpalmdaleca.gov
              </a>
            </div>
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Council meetings & agendas</h3>
              <p className="text-on-surface-variant mb-3">Council agendas, recordings, and city government information.</p>
              <a
                href="https://www.cityofpalmdaleca.gov/"
                target="_blank"
                rel="noreferrer"
                className="text-primary font-semibold"
              >
                Council resources
              </a>
            </div>
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Los Angeles County services</h3>
              <p className="text-on-surface-variant">County-wide services and resources for {siteConfig.city} residents.</p>
              <p className="text-on-surface-variant mt-2">LA County Government services and information.</p>
              <a
                href="https://www.lacounty.gov/"
                target="_blank"
                rel="noreferrer"
                className="text-primary font-semibold block mt-2"
              >
                Visit LA County
              </a>
            </div>
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Antelope Valley Transportation Authority</h3>
              <p className="text-on-surface-variant">Public transit and transportation services for the Antelope Valley.</p>
              <p className="text-on-surface-variant mt-2">{siteConfig.city} Transportation Center is a key hub.</p>
            </div>
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Joe Davies Heritage Airpark</h3>
              <p className="text-on-surface-variant">2001 E Ave P, {siteConfig.city}, {siteConfig.state} 93550</p>
              <p className="text-on-surface-variant mt-2">Public aviation museum and community landmark.</p>
            </div>
            <div className="card bg-surface-container-low">
              <h3 className="text-lg font-semibold mb-2">Emergency & public safety</h3>
              <p className="text-on-surface-variant">Emergency: 911</p>
              <p className="text-on-surface-variant">Sheriff Station: (661) 272-2400</p>
              <p className="text-on-surface-variant">LA County Fire: Multiple stations serving {siteConfig.city}</p>
            </div>
          </div>
        </div>
      </section>

      {/* City Hall Contact */}
      <section className="py-16 bg-surface-container-low">
        <div className="container mx-auto px-4">
          <div className="card bg-surface">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-6 h-6 text-primary" />
                  <h2 className="text-2xl font-display font-bold">City Hall main contact</h2>
                </div>
                <p className="text-on-surface-variant">38300 Sierra Highway, {siteConfig.city}, {siteConfig.state} 93550</p>
                <p className="text-on-surface-variant">Phone: (661) 267-5100</p>
                <p className="text-on-surface-variant">Hours: Monday-Thursday 7:30 AM - 6:00 PM (Closed Fridays)</p>
                <p className="text-on-surface-variant">Website: cityofpalmdaleca.gov</p>
              </div>
              <div className="flex flex-col gap-3">
                <a href="tel:+16612675100" className="btn-primary">Call City Hall</a>
                <a
                  href="https://www.cityofpalmdaleca.gov/"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline"
                >
                  Visit City Website
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4">
          <div className="card bg-surface-container-low flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-display font-bold mb-2">Need more help?</h2>
              <p className="text-on-surface-variant">
                Reach out to our team and we will help you find the right contact.
              </p>
            </div>
            <Link to="/contact" className="btn-primary">Contact us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
